import { injectEndpoints } from '../../../../service/service';
import {
  CompleteSignUpPayload,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  ResetPasswordPayload,
  SignInByEmailPayload,
  SignInByEmailResponse,
  SignUpByEmailPayload,
  SignUpByEmailResponse,
  SignUpConfigResponse,
  User,
  VerifySignUpOtpPayload,
  VerifySignUpOtpResponse,
  UserProfile,
  RespondNewPasswordChallengePayload,
} from './Auth.types';
import { getSha256 } from '../../../../util/helpers';

export const AuthEndpoint = injectEndpoints({
  endpoints: (builder) => ({
    getMe: builder.query<User, void>({
      query: () => {
        return {
          method: 'GET',
          url: '/v2/me',
        };
      },
    }),
    // The same /v2/me read as getMe, as a separate endpoint. The auth listener
    // treats a successful getMe as a sign-in and starts the app routing; the
    // reads before and after sign-up completion and after sign-in must not.
    readMe: builder.query<User, void>({
      query: () => {
        return {
          method: 'GET',
          url: '/v2/me',
        };
      },
    }),
    signInByEmail: builder.mutation<SignInByEmailResponse, SignInByEmailPayload>({
      query: ({ password, ...body }) => {
        return {
          method: 'POST',
          url: '/v2/sign-in',
          body: {
            password: getSha256(password),
            ...body,
          },
        };
      },
    }),
    respondNewPasswordChallenge: builder.mutation<SignInByEmailResponse, RespondNewPasswordChallengePayload>({
      query: ({ password, ...body }) => {
        return {
          method: 'POST',
          url: '/v2/sign-in/respond-challenge',
          body: {
            ...body,
            password: getSha256(password),
          },
        };
      },
    }),
    forgotPassword: builder.mutation<ForgotPasswordResponse, ForgotPasswordPayload>({
      query: (body) => {
        return {
          method: 'POST',
          url: '/v2/forgot-password',
          body,
        };
      },
    }),
    resetPassword: builder.mutation<unknown, ResetPasswordPayload>({
      query: ({ password, email, code }) => {
        return {
          method: 'POST',
          url: '/v2/reset-password',
          body: {
            password: getSha256(password),
            email,
            code,
          },
        };
      },
    }),
    signUpByEmail: builder.mutation<SignUpByEmailResponse, SignUpByEmailPayload>({
      query: ({ password, ...body }) => {
        return {
          method: 'POST',
          url: '/v2/sign-up',
          body: {
            password: getSha256(password),
            ...body,
          },
        };
      },
    }),
    verifySignUpOtp: builder.mutation<VerifySignUpOtpResponse, VerifySignUpOtpPayload>({
      query: ({ password, ...body }) => {
        return {
          method: 'POST',
          url: '/v2/otp-verification',
          body: {
            password: getSha256(password),
            ...body,
          },
        };
      },
    }),
    getSignUpConfig: builder.query<SignUpConfigResponse, void>({
      query: () => {
        return {
          method: 'GET',
          url: '/v2/sign-up-config',
        };
      },
    }),
    completeSignUp: builder.mutation<unknown, CompleteSignUpPayload>({
      query: (body) => {
        return {
          method: 'POST',
          url: '/v2/complete-sign-up',
          body,
        };
      },
    }),
    switchRole: builder.query({
      query: (arg) => {
        const { roleId } = arg;
        return {
          method: 'GET',
          url: `/v2/role/${roleId}`,
        };
      },
    }),
    handleSSO: builder.mutation<unknown, CompleteSignUpPayload>({
      query: (body) => {
        return {
          method: 'POST',
          url: '/v2/sso',
          body,
        };
      },
    }),
    updateUserProfile: builder.mutation<unknown, UserProfile>({
      query: (body) => {
        return {
          method: 'POST',
          url: '/v2/user-profile',
          body,
        };
      },
    }),
  }),
});

export const {
  useGetMeQuery,
  useSignInByEmailMutation,
  useLazyGetMeQuery,
  useLazyReadMeQuery,
  useLazyGetSignUpConfigQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useSignUpByEmailMutation,
  useVerifySignUpOtpMutation,
  useCompleteSignUpMutation,
  useLazySwitchRoleQuery,
  useGetSignUpConfigQuery,
  useHandleSSOMutation,
  useUpdateUserProfileMutation,
  useRespondNewPasswordChallengeMutation,
} = AuthEndpoint;
